/**
 * llmService.js
 * Client-side service for communicating with the local LLM evaluate endpoint
 */

import { resolveBackendApiBaseUrl } from '../utils/runtimeEnv.js';
import { getBackendAuthHeaders } from '../utils/backendAuth.js';

// LLM evaluation endpoint is always served by the backend app.
const API_BASE = resolveBackendApiBaseUrl();

/**
 * Pretty-print an object or error for display
 */
const prettyPrintValue = (value) => {
  if (value === null || value === undefined) {
    return String(value);
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch (_e) {
      return String(value);
    }
  }
  return String(value);
};

/**
 * Send prompt data and user context to the local LLM evaluate endpoint
 * @param {string} prompt - The user's custom instruction or query
 * @param {object} data - Additional context (userId, sessionId, etc.)
 * @returns {Promise<object>} Response with structured_output or completion
 */
export const evaluatePrompt = async (prompt, data = {}) => {
  const url = `${API_BASE}/api/llm-evaluate`;

  console.log('LLM Service: Sending request to', url, {
    prompt: prompt.substring(0, 100) + (prompt.length > 100 ? '...' : ''),
    userId: data.userId
  });

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: await getBackendAuthHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        prompt,
        data,
        userId: data.userId
      })
    });

    if (!response.ok) {
      let errorDetails = null;
      try {
        // Read response as text first, then try to parse as JSON
        const responseText = await response.text();
        try {
          errorDetails = JSON.parse(responseText);
        } catch (_parseError) {
          errorDetails = {
            status: response.status,
            message: responseText || `HTTP ${response.status}`
          };
        }
      } catch (_e) {
        errorDetails = {
          status: response.status,
          message: `Failed to read error response: ${_e.message}`
        };
      }

      console.error('LLM Service Error:', response.status, errorDetails);
      throw new Error(
        `LLM evaluation failed (${response.status}): ${prettyPrintValue(errorDetails)}`
      );
    }

    const result = await response.json();
    console.log('LLM Service: Response received', {
      has_structured_output: !!result.structured_output,
      repaired: result.structured_output_repaired,
      completion_length: result.completion?.length || 0,
      analyzed_count: result.analyzedPromptCount
    });

    return result;
  } catch (error) {
    console.error('LLM Service: Request failed', error.message);
    throw error;
  }
};

const llmService = {
  evaluatePrompt
};

export default llmService;
